/**
 * Impersonation session client.
 *
 * These calls go to /api/admin/v1/ — the auth-layer identity swap is gated
 * to /api/portal/ on the backend, so these requests always run as the real
 * super admin, including when an active session exists (so "End Session"
 * works correctly from inside an impersonated browser tab).
 */
import { authenticatedFetch } from '../utils/authFetch';
import { AppConfig } from '../../Config';

const ENDPOINT = `${AppConfig.api.apiUrl}/api/admin/v1/impersonation-sessions/`;

export interface ImpersonationSessionPayload {
  session_id: number;
  real_user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
}

interface BackendSessionResponse {
  id: number;
  real_account: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  target_account: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  started_at: string;
  ended_at: string | null;
  reason: string;
}

function toHydratePayload(resp: BackendSessionResponse): ImpersonationSessionPayload {
  return {
    session_id: resp.id,
    real_user: {
      id: resp.real_account.id,
      email: resp.real_account.email,
      first_name: resp.real_account.first_name,
      last_name: resp.real_account.last_name,
    },
  };
}

export async function startImpersonationSession(
  targetAccountId: number,
  reason: string,
): Promise<ImpersonationSessionPayload> {
  const response = await authenticatedFetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      target_account_id: targetAccountId,
      reason,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to start impersonation (${response.status}): ${text}`);
  }
  const data = (await response.json()) as BackendSessionResponse;
  return toHydratePayload(data);
}

export async function endImpersonationSession(sessionId: number): Promise<void> {
  const response = await authenticatedFetch(`${ENDPOINT}${sessionId}/`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to end impersonation (${response.status}): ${text}`);
  }
}
